import React, { useState, ChangeEvent, useEffect, useCallback } from 'react';
import style from './benificiary.module.css';
import { Layout } from "../../GlobalComponent/Layout/Layout";
import { getCompanyList } from "./action/getAllCompany";
import { getAllDataForQuery, searchCompanyList, searchBenHosp, getAllHospitalMapData } from "./action/searchcompany";
import { ShowAlert } from '../../GlobalComponent/Alerts/Alert';
import { Box, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { PaginationDropdown } from '../../GlobalComponent/PaginationDropdown/PaginationDropdown';
import { PageSizeDropdown } from '../../GlobalComponent/PageSizeDropdown/PageSizeDropdown';
import SearchableSelect from '../../GlobalComponent/SearchableSelect/SearchableSelect';

interface Hospital {
    _id: string;
    companyName: string;
    subscriptionType: string;
    dateOfPartnership: string;
    isActivePartnership: boolean;
    isDeleted: boolean;
    __v: number;
}

interface FindData {
    _id: string;
    subscriberId: string;
    userId: string;
    personalInfo: Record<string, any>;
    login: boolean;
    address: Record<string, any>;
    medicalInfo: any[];
    insurancePolicies: any[];
    emergencyContacts: any[];
    consentsGivenTo: any[];
    havingConsentsOf: any[];
    isDeleted: boolean;
    progress: number;
    createdAt: string;
    updatedAt: string;
    __v: number;
}

interface Option {
    label: string;
    value: string;
}

function getCookie(name: string) {
    const cookies = document.cookie.split('; ');
    for (let cookie of cookies) {
        const [key, value] = cookie.split('=');
        if (key === name && value === "true") {
            return true;
        }
    }
    return false;
}

export const Beneficiary: React.FC = () => {
    const [selectedTab, setSelectedTab] = useState(0);

    // Tab 1 state (Hospital Not Found)
    const [filteredData, setFilteredData] = useState<FindData[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [hasNextPage, setHasNextPage] = useState(false)
    const [isLoading, setIsLoading] = useState(false);
    const [radius, setRadius] = useState<number>(0);

    // Tab 2 state (Hospital Mapping)
    const [filterBenData, setFilterBenData] = useState<any[]>([]);
    const [currentPageTab2, setCurrentPageTab2] = useState<number>(1);
    const [isLoadingTab2, setIsLoadingTab2] = useState(false);
    const [totalCountTab2, setTotalCountTab2] = useState<number>(0);
    const [pageSizeTab2, setPageSizeTab2] = useState<number>(10);

    // Shared state
    const [companyOptions, setCompanyOptions] = useState<Option[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<string>('');
    const [clearKey, setClearKey] = useState<number>(0);

    const increaseRadius = (): void => {
        if (radius <= 99) {
            setRadius(prevRadius => prevRadius + 1);
        }
    };

    const decreaseRadius = (): void => {
        setRadius(prevRadius => Math.max(prevRadius - 1, 0));
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        let replaced: number = Number(value.replace(/^0+/, ''));
        event.target.value = replaced + "";
        if (!isNaN(replaced) && replaced >= 0 && replaced <= 100) {
            setRadius(replaced);
        }
    };

    const handleClearSearch = (): void => {
        setRadius(0);
        setFilteredData([]);
        setCurrentPage(1);
        setFilterBenData([]);
        setCurrentPageTab2(1);
        setSelectedCompany('');
        setClearKey(prev => prev + 1);
    };

    // Tab 1 handlers
    const handleSearchChange = (page: number): void => {
        setCurrentPage(1)
        setFilteredData([])
        fetchingSearch(page);
    };

    const handleButtonChange = (page: number): void => {
        fetchingSearch(page);
    };

    // Tab 2 handlers
    const handleSearchChangeTab2 = (page: number): void => {
        setCurrentPageTab2(1)
        setFilterBenData([])
        fetchingBenSearch(page);
    };

    const handlePageSizeChangeTab2 = (value: number) => {
        setPageSizeTab2(value);
        setCurrentPageTab2(1);
        fetchingBenSearchWithSize(1, value);
    };

    const handleCustomPageChangeTab2 = (pageNumber: number) => {
        setCurrentPageTab2(pageNumber);
        fetchingBenSearch(pageNumber);
    };

    const totalPagesTab2 = Math.ceil(totalCountTab2 / pageSizeTab2);

    const sendQueryResultOnExcel = async () => {
        try {
            if (!selectedCompany) {
                alert("Please select company name");
                return;
            };
            if (radius <= 0) {
                alert("Please radius value should be greater than zero");
                return;
            }
            const company = companyOptions.filter((item) => item.value === selectedCompany);
            if (company.length === 0) {
                alert("Please select company name");
                return;
            }
            const res = await getAllDataForQuery(selectedCompany, radius, company[0].label);
            alert(res)
        } catch (err: any) {
            ShowAlert("warning", err?.response?.data?.message.join(",") || "Error fetching search results:");
        }
    }

    const sendResultOnExcel = async () => {
        try {
            if (!selectedCompany) {
                alert("Please select company name");
                return;
            };
            const company = companyOptions.filter((item) => item.value === selectedCompany);
            if (company.length === 0) {
                alert("Please select company name");
                return;
            }
            const res = await getAllHospitalMapData(selectedCompany, company[0].label);
            alert(res)
        } catch (err: any) {
            ShowAlert("warning", err?.response?.data?.message.join(",") || "Error fetching search results:");
        }
    }

    const fetchingSearch = useCallback(async (page: number): Promise<void> => {
        try {
            setIsLoading(true)
            if (!selectedCompany) {
                alert("Please select company name");
                return;
            };
            if (radius <= 0) {
                alert("Please radius value should be greater than zero");
                return;
            }
            const listResponse = await searchCompanyList(selectedCompany, 10, page || currentPage, radius);
            setHasNextPage(listResponse.hasNextPage)
            setFilteredData(listResponse.data);
        } catch (err: any) {
            ShowAlert("warning", err?.response?.data?.message.join(",") || "Error fetching search results:");
        } finally {
            setIsLoading(false)
        }
    }, [selectedCompany, currentPage, radius]);

    const fetchingBenSearch = useCallback(async (page: number): Promise<void> => {
        try {
            setIsLoadingTab2(true)
            if (!selectedCompany) {
                alert("Please select company name");
                return;
            };
            const listResponse = await searchBenHosp(selectedCompany, pageSizeTab2, page || currentPageTab2);
            setFilterBenData(listResponse.data);
            setTotalCountTab2(listResponse.total || 0);
        } catch (err: any) {
            ShowAlert("warning", err?.response?.data?.message.join(",") || "Error fetching search results:");
        } finally {
            setIsLoadingTab2(false)
        }
    }, [selectedCompany, currentPageTab2, pageSizeTab2]);

    const fetchingBenSearchWithSize = useCallback(async (page: number, pageSize: number): Promise<void> => {
        try {
            setIsLoadingTab2(true)
            if (!selectedCompany) {
                alert("Please select company name");
                return;
            };
            const listResponse = await searchBenHosp(selectedCompany, pageSize, page);
            setFilterBenData(listResponse.data);
            setTotalCountTab2(listResponse.total || 0);
        } catch (err: any) {
            ShowAlert("warning", err?.response?.data?.message.join(",") || "Error fetching search results:");
        } finally {
            setIsLoadingTab2(false)
        }
    }, [selectedCompany]);

    const fetchData = async () => {
        try {
            const response = await getCompanyList();
            const companyList = response?.data?.map((item: Hospital) => ({
                label: item.companyName,
                value: item._id
            }));
            setCompanyOptions(companyList);
        } catch (err) {
            console.error("Error fetching hospital data:", err);
            ShowAlert("warning", "Error fetching hospital data");
        }
    };

    useEffect(() => {
        fetchData()
    }, []);

    const handleTabChange = (index: number) => {
        setSelectedTab(index);
        handleClearSearch();
    };

    return (
        <Layout>
            <Box className={style.tabsContainer} style={{ width: "100%", height: "50px" }}>
                <Tabs index={selectedTab} onChange={handleTabChange} variant="soft-rounded" colorScheme="green">
                    <TabList>
                        <Tab>Hospital Not Found</Tab>
                        <Tab>Hospital Mapping</Tab>
                    </TabList>
                    <TabPanels>
                        <TabPanel p={0}>
                            <div className={style.container}>
                                <h1 className={style.title}>Beneficiary Having No Hospital Dashboard Details</h1>
                                <div className={style.formContainer}>
                                    <div className={style.dropdownContainer}>
                                        <label htmlFor="hospital-select" className={style.label}>Select Company</label>
                                        <SearchableSelect
                                            key={`tab1-${selectedTab}-${clearKey}`}
                                            options={companyOptions}
                                            placeholder="Search Company"
                                            onSelect={setSelectedCompany}
                                            value={selectedCompany}
                                        />
                                    </div>
                                    <div className={style.radiusControls}>
                                        <p className={style.radiusLabel}>  Km</p>
                                        <button onClick={decreaseRadius}>-</button>
                                        <input
                                            type="number"
                                            value={radius}
                                            onChange={handleInputChange}
                                            placeholder='Enter radius'
                                            max="100"
                                            className={style.radiusInput}
                                        />
                                        <button onClick={increaseRadius}>+</button>
                                    </div>
                                    <div className={style.searchContainer}>
                                        <button onClick={() => handleSearchChange(1)} className={style.searchButton} disabled={isLoading}>{isLoading ? "Loading..." : "Search"}</button>
                                        <button onClick={sendQueryResultOnExcel} className={style.searchButton}>Get All Pages On Email</button>
                                        <button onClick={handleClearSearch} className={style.clearButton}>Clear Search</button>
                                    </div>
                                </div>
                                {filteredData.length > 0 && <div className={style.paginationContainer}>
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                        <button
                                            disabled={currentPage <= 1 || isLoading}
                                            className={style.pageButton}
                                            onClick={() => {
                                                const newPage = currentPage - 1;
                                                setCurrentPage(newPage);
                                                handleButtonChange(newPage);
                                            }}
                                        >
                                            Previous
                                        </button>
                                        <h3 style={{ marginLeft: "10px", marginRight: "10px" }}>{isLoading ? "Loading..." : currentPage}</h3>
                                        <button
                                            disabled={!hasNextPage || isLoading}
                                            className={style.pageButton}
                                            onClick={() => {
                                                const newPage = currentPage + 1;
                                                setCurrentPage(newPage);
                                                handleButtonChange(newPage);
                                            }}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>}
                                <div className={style.tableContainer}>
                                    {filteredData.length === 0 ? (
                                        <h1 className={style.absence}>No Data available</h1>
                                    ) : (
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>BenID</th>
                                                    <th>SubscriberId</th>
                                                    <th>Beneficiary Name</th>
                                                    <th>Mobile</th>
                                                    <th>Relationship</th>
                                                    <th>Address1</th>
                                                    <th>Address2</th>
                                                    <th>Address3</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredData.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{item._id}</td>
                                                        <td>{item.subscriberId}</td>
                                                        <td>{item?.personalInfo?.fullName || "Not available"}</td>
                                                        <td>{item?.personalInfo?.mobile?.number || "Not available"}</td>
                                                        <td>{item?.personalInfo?.relationshipToSubscriber || "Not available"}</td>
                                                        <td>{(item?.address[0]?.addressLine1 + ", " + item?.address[0]?.city + ", " + item?.address[0]?.pinCode + ", " + item?.address[0]?.country)?.toLocaleLowerCase() || "Not available"}</td>
                                                        {!item.address[1] ? <td>N/A</td> : <td>{(item?.address[1]?.addressLine1 + ", " + item?.address[1]?.city + ", " + item?.address[1]?.pinCode + ", " + item?.address[1]?.country)?.toLocaleLowerCase() || "Not available"}</td>}
                                                        {!item.address[2] ? <td>N/A</td> : <td>{(item?.address[2]?.addressLine1 + ", " + item?.address[2]?.city + ", " + item?.address[2]?.pinCode + ", " + item?.address[2]?.country)?.toLocaleLowerCase() || "Not available"}</td>}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </TabPanel>
                        <TabPanel p={0}>
                            <div className={style.container}>
                                <h1 className={style.title}>Beneficiaries with Preferred/Nearest Hospital Details</h1>
                                <div className={style.formContainer}>
                                    <div className={style.dropdownContainer}>
                                        <label className={style.label}>Select Company</label>
                                        <SearchableSelect
                                            key={`tab2-${selectedTab}-${clearKey}`}
                                            options={companyOptions}
                                            placeholder="Search Company"
                                            onSelect={setSelectedCompany}
                                            value={selectedCompany}
                                        />
                                    </div>
                                    <div className={style.searchContainer}>
                                        <button onClick={() => handleSearchChangeTab2(1)} className={style.searchButton} disabled={isLoadingTab2}>{isLoadingTab2 ? "Loading..." : "Search"}</button>
                                        {getCookie("isSuperAdmin") && <button onClick={sendResultOnExcel} className={style.searchButton}>Get All Pages On Email</button>}
                                        <button onClick={handleClearSearch} className={style.clearButton}>Clear Search</button>
                                    </div>
                                </div>
                                {filterBenData.length > 0 && <div className={style.paginationContainer}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                            <span>Page size:</span>
                                            <PageSizeDropdown
                                                pageSize={pageSizeTab2}
                                                onPageSizeChange={handlePageSizeChangeTab2}
                                            />
                                            <span>Go to page:</span>
                                            <PaginationDropdown
                                                totalPages={totalPagesTab2}
                                                currentPage={currentPageTab2}
                                                onPageChange={handleCustomPageChangeTab2}
                                            />
                                            <span>of {totalPagesTab2}</span>
                                        </div>
                                    </div>
                                </div>}
                                <div className={style.tableContainer}>
                                    {filterBenData.length === 0 ? (
                                        <h1 className={style.absence}>No Data available</h1>
                                    ) : (
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>BenID</th>
                                                    <th>SubscriberId</th>
                                                    <th>Beneficiary Name</th>
                                                    <th>Address1</th>
                                                    <th>Category1</th>
                                                    <th>Hospital1</th>
                                                    <th>Distance1</th>
                                                    <th>Address2</th>
                                                    <th>Category2</th>
                                                    <th>Hospital2</th>
                                                    <th>Distance2</th>
                                                    <th>Address3</th>
                                                    <th>Category3</th>
                                                    <th>Hospital3</th>
                                                    <th>Distance3</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filterBenData.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{item.benId}</td>
                                                        <td>{item.subId}</td>
                                                        <td>{item?.benName || "Not available"}</td>
                                                        <td>{item?.address1?.address || "Not available"}</td>
                                                        <td>{item?.address1?.category || "Not available"}</td>
                                                        <td>{item?.address1?.hospitalName || "Not available"}</td>
                                                        <td>{item?.address1?.distance || "Not available"}</td>
                                                        <td>{item?.address2?.address || "Not available"}</td>
                                                        <td>{item?.address2?.category || "Not available"}</td>
                                                        <td>{item?.address2?.hospitalName || "Not available"}</td>
                                                        <td>{item?.address2?.distance || "Not available"}</td>
                                                        <td>{item?.address3?.address || "Not available"}</td>
                                                        <td>{item?.address3?.category || "Not available"}</td>
                                                        <td>{item?.address3?.hospitalName || "Not available"}</td>
                                                        <td>{item?.address3?.distance || "Not available"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </Box>
        </Layout>
    );
};